import { useCallback, useContext, useEffect, useState } from "react";
import { downloadFile } from "../../utils/download";
import { Button, SettingsCard } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import "./TranslationSettings.prefix.css";

const block = cn("translation-settings");

/** Project has a TextArea control → translation tools are relevant. */
export const projectHasTextArea = (project) => /<TextArea\b/i.test(project?.label_config || "");

const Input = (props) => <input className={block.elem("input").toClassName()} {...props} />;

export const TranslationSettings = () => {
  const { project } = useContext(ProjectContext);
  const api = useAPI();
  const pk = project?.id;

  const [tm, setTm] = useState({ count: 0, results: [] });
  const [tmQuery, setTmQuery] = useState("");
  const [tmForm, setTmForm] = useState({ source_text: "", target_text: "" });
  const [glossary, setGlossary] = useState([]);
  const [glForm, setGlForm] = useState({ source_term: "", target_term: "", note: "" });
  const [candidates, setCandidates] = useState(null);
  const [sourceField, setSourceField] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [suggestOn, setSuggestOn] = useState(true);
  const [scope, setScope] = useState("org");
  const [sources, setSources] = useState([]);
  const [orgProjects, setOrgProjects] = useState([]);

  useEffect(() => {
    if (!project) return;
    setSuggestOn(project.translation_suggestions_enabled !== false);
    setScope(project.translation_tm_scope || "org");
    setSources(Array.isArray(project.translation_tm_sources) ? project.translation_tm_sources : []);
  }, [project?.id, project?.translation_suggestions_enabled, project?.translation_tm_scope]);

  useEffect(() => {
    if (!pk) return;
    api.callApi("projects", { params: { page_size: 500 }, suppressError: true }).then((res) => {
      const list = res?.results ?? (Array.isArray(res) ? res : []);
      setOrgProjects(list.filter((p) => p.id !== pk).map((p) => ({ id: p.id, title: p.title })));
    });
  }, [api, pk]);

  const loadTm = useCallback(async () => {
    if (!pk) return;
    const res = await api.callApi("projectTranslationMemory", {
      params: { pk, q: tmQuery || undefined },
      suppressError: true,
    });
    if (res && !res.error) setTm({ count: res.count ?? 0, results: res.results ?? [] });
  }, [api, pk, tmQuery]);

  const loadGlossary = useCallback(async () => {
    if (!pk) return;
    const res = await api.callApi("projectGlossary", { params: { pk }, suppressError: true });
    if (res && !res.error) setGlossary(res.results ?? []);
  }, [api, pk]);

  useEffect(() => {
    loadTm();
  }, [loadTm]);
  useEffect(() => {
    loadGlossary();
  }, [loadGlossary]);

  if (!pk) return null;

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(null), 4000);
  };

  const addTm = async () => {
    if (!tmForm.source_text.trim() || !tmForm.target_text.trim()) return;
    await api.callApi("projectTranslationMemoryAdd", { params: { pk }, body: tmForm });
    setTmForm({ source_text: "", target_text: "" });
    loadTm();
  };
  const delTm = async (id) => {
    await api.callApi("projectTranslationMemoryDelete", { params: { pk, entry_id: id } });
    loadTm();
  };

  const importTmx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.callApi("projectTranslationTmxImport", { params: { pk }, body: fd });
    setBusy(false);
    e.target.value = "";
    if (res && !res.error) flash(`Imported ${res.imported} of ${res.parsed} segments.`);
    loadTm();
  };
  const exportTmx = () =>
    void downloadFile(`/api/projects/${pk}/translation/memory/export-tmx`, `translation-memory-${pk}.tmx`);

  const backfill = async () => {
    if (!sourceField.trim()) {
      flash("Enter the task data field that holds the source text.");
      return;
    }
    setBusy(true);
    const res = await api.callApi("projectTranslationBackfill", {
      params: { pk },
      body: { source_field: sourceField.trim() },
    });
    setBusy(false);
    if (res && !res.error) flash(`Added ${res.added} segments to the memory.`);
    loadTm();
  };

  const addGl = async () => {
    if (!glForm.source_term.trim() || !glForm.target_term.trim()) return;
    await api.callApi("projectGlossaryAdd", { params: { pk }, body: glForm });
    setGlForm({ source_term: "", target_term: "", note: "" });
    loadGlossary();
  };
  const delGl = async (id) => {
    await api.callApi("projectGlossaryDelete", { params: { pk, term_id: id } });
    loadGlossary();
  };

  const toggleSuggestions = async () => {
    const next = !suggestOn;
    setSuggestOn(next);
    await api.callApi("updateProject", { params: { pk }, body: { translation_suggestions_enabled: next } });
    flash(next ? "Inline suggestions enabled." : "Inline suggestions hidden from annotators.");
  };

  const changeScope = async (val) => {
    setScope(val);
    await api.callApi("updateProject", { params: { pk }, body: { translation_tm_scope: val } });
  };
  const toggleSource = async (id) => {
    const next = sources.includes(id) ? sources.filter((x) => x !== id) : [...sources, id];
    setSources(next);
    await api.callApi("updateProject", { params: { pk }, body: { translation_tm_sources: next } });
  };

  const extract = async (save) => {
    setBusy(true);
    const res = await api.callApi("projectGlossaryExtract", {
      params: { pk },
      body: { min_count: 3, max_terms: 50, save: !!save },
    });
    setBusy(false);
    if (res && !res.error) {
      setCandidates(res.candidates ?? []);
      if (save) {
        flash(`Saved ${res.saved} new terms.`);
        loadGlossary();
      }
    }
  };

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title="Translation"
        subtitle="Translation Memory and glossary, fuzzy-match suggestions and preferred terms shown to annotators while they translate."
      />
      {msg && <div className={block.elem("flash").toClassName()}>{msg}</div>}

      <label className={block.elem("switch").toClassName()}>
        <input type="checkbox" checked={suggestOn} onChange={toggleSuggestions} />
        <span>
          <strong>Show inline suggestions to annotators</strong>
          <span className={block.elem("switch-sub").toClassName()}>
            Display Translation Memory matches + glossary terms under the TextArea while they translate.
          </span>
        </span>
      </label>

      {suggestOn && (
        <div className={block.elem("sources").toClassName()}>
          <span className={block.elem("sources-label").toClassName()}>Suggestion sources</span>
          <select
            className={block.elem("input").toClassName()}
            style={{ flex: "0 0 auto", maxWidth: 240 }}
            value={scope}
            onChange={(e) => changeScope(e.target.value)}
          >
            <option value="project">This project only</option>
            <option value="org">Entire organization</option>
            <option value="selected">Selected projects</option>
          </select>
          {scope === "selected" && (
            <div className={block.elem("project-list").toClassName()}>
              {orgProjects.map((p) => (
                <label key={p.id} className={block.elem("project-item").toClassName()}>
                  <input type="checkbox" checked={sources.includes(p.id)} onChange={() => toggleSource(p.id)} />
                  <span>{p.title}</span>
                </label>
              ))}
              {orgProjects.length === 0 && (
                <span className={block.elem("switch-sub").toClassName()}>No other projects in this organization.</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className={cn("settings-page").elem("body").toClassName()}>
        <SettingsCard
          title="Translation Memory"
          description="Reusable source→target segments offered as fuzzy matches during translation."
        >
          <div className={block.elem("row").toClassName()}>
            <Input
              placeholder="Source text"
              value={tmForm.source_text}
              onChange={(e) => setTmForm({ ...tmForm, source_text: e.target.value })}
            />
            <Input
              placeholder="Target text"
              value={tmForm.target_text}
              onChange={(e) => setTmForm({ ...tmForm, target_text: e.target.value })}
            />
            <Button look="filled" variant="primary" size="small" onClick={addTm}>
              Add
            </Button>
          </div>

          <div className={block.elem("toolbar").toClassName()}>
            <Input
              placeholder="Search source…"
              value={tmQuery}
              onChange={(e) => setTmQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadTm()}
            />
            <Button look="outlined" variant="neutral" size="small" onClick={loadTm}>
              Search
            </Button>
            <label className={block.elem("file").toClassName()}>
              <input type="file" accept=".tmx,application/xml" onChange={importTmx} hidden />
              <span>Import TMX</span>
            </label>
            <Button look="outlined" variant="neutral" size="small" onClick={exportTmx}>
              Export TMX
            </Button>
          </div>

          <div className={block.elem("backfill").toClassName()}>
            <Input
              placeholder="Task data field with the source text (e.g. src)"
              value={sourceField}
              onChange={(e) => setSourceField(e.target.value)}
            />
            <Button look="outlined" variant="neutral" size="small" waiting={busy} onClick={backfill}>
              Backfill from translations
            </Button>
          </div>

          <div className={block.elem("count").toClassName()}>
            {tm.count} segment{tm.count === 1 ? "" : "s"}
          </div>
          <div className={block.elem("list").toClassName()}>
            {tm.results.map((r) => (
              <div key={r.id} className={block.elem("item").toClassName()}>
                <span className={block.elem("src").toClassName()}>{r.source_text}</span>
                <span className={block.elem("arrow").toClassName()}>→</span>
                <span className={block.elem("tgt").toClassName()}>{r.target_text}</span>
                <Button type="button" variant="negative" look="outlined" size="smaller" onClick={() => delTm(r.id)}>
                  Delete
                </Button>
              </div>
            ))}
            {tm.results.length === 0 && (
              <div className={block.elem("empty").toClassName()}>
                No segments yet. Add one, import a TMX, or backfill from existing translations.
              </div>
            )}
          </div>
        </SettingsCard>

        <SettingsCard
          title="Glossary"
          description="Preferred source→target terms enforced for consistency, shown to annotators inline."
        >
          <div className={block.elem("row").toClassName()}>
            <Input
              placeholder="Source term"
              value={glForm.source_term}
              onChange={(e) => setGlForm({ ...glForm, source_term: e.target.value })}
            />
            <Input
              placeholder="Target term"
              value={glForm.target_term}
              onChange={(e) => setGlForm({ ...glForm, target_term: e.target.value })}
            />
            <Input
              placeholder="Note"
              value={glForm.note}
              onChange={(e) => setGlForm({ ...glForm, note: e.target.value })}
            />
            <Button look="filled" variant="primary" size="small" onClick={addGl}>
              Add
            </Button>
          </div>

          <div className={block.elem("toolbar").toClassName()}>
            <Button look="outlined" variant="neutral" size="small" waiting={busy} onClick={() => extract(false)}>
              Suggest terms from memory
            </Button>
            {candidates && candidates.length > 0 && (
              <Button look="filled" variant="primary" size="small" onClick={() => extract(true)}>
                Save {candidates.length} suggestions
              </Button>
            )}
          </div>

          {candidates && candidates.length > 0 && (
            <div className={block.elem("candidates").toClassName()}>
              {candidates.slice(0, 30).map((c, i) => (
                <span key={i} className={block.elem("chip").toClassName()} title={`seen ${c.count}×`}>
                  {c.source_term}
                  {c.target_term ? ` → ${c.target_term}` : ""}
                </span>
              ))}
            </div>
          )}

          <div className={block.elem("list").toClassName()}>
            {glossary.map((t) => (
              <div key={t.id} className={block.elem("item").toClassName()}>
                <span className={block.elem("src").toClassName()}>{t.source_term}</span>
                <span className={block.elem("arrow").toClassName()}>→</span>
                <span className={block.elem("tgt").toClassName()}>{t.target_term}</span>
                {t.note && <span className={block.elem("note").toClassName()}>{t.note}</span>}
                <Button type="button" variant="negative" look="outlined" size="smaller" onClick={() => delGl(t.id)}>
                  Delete
                </Button>
              </div>
            ))}
            {glossary.length === 0 && (
              <div className={block.elem("empty").toClassName()}>
                No terms yet. Add one or extract candidates from the memory.
              </div>
            )}
          </div>
        </SettingsCard>
      </div>
    </div>
  );
};

TranslationSettings.title = "Translation";
TranslationSettings.path = "/translation";
